/* First-party cookieless or explicit-consent analytics. No form values, persistent identifiers in cookieless mode, or question text. */
const env = process.env;
const CONSENT_KEY = "karalewich_analytics_consent_v1";
const EXCLUSION_KEY = "karalewich_analytics_excluded";
const VISITOR_KEY = "karalewich_visitor_v1";
const SESSION_KEY = "karalewich_session_v1";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const VISITOR_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;
const trackingMode = env.REACT_APP_ANALYTICS_MODE === "cookieless" ? "cookieless" : "consent";
const gaId = trackingMode !== "cookieless" && /^G-[A-Z0-9]+$/.test(env.REACT_APP_GA_MEASUREMENT_ID ?? "")
  ? env.REACT_APP_GA_MEASUREMENT_ID
  : "";
const endpoint = env.REACT_APP_ANALYTICS_ENDPOINT?.trim() || "";
const localEnabled = env.REACT_APP_ENABLE_LOCAL_ANALYTICS === "true";
const debug = env.REACT_APP_ANALYTICS_DEBUG === "true";

export const EVENT_NAMES = new Set([
  "page_view",
  "cta_click",
  "nav_click",
  "link_click",
  "email_click",
  "phone_click",
  "demo_start",
  "demo_mode_change",
  "demo_question",
  "demo_source_open",
  "demo_action",
  "form_start",
  "form_submit",
  "form_error",
  "form_email_draft",
  "scroll_depth",
  "engagement",
  "project_open",
  "service_view",
  "service_select",
  "work_open",
  "faq_open",
  "section_view",
  "web_vital",
  "client_error",
  "game_open",
  "game_complete",
]);
const STRING_PARAMS = new Set([
  "placement",
  "label",
  "target",
  "section",
  "service",
  "service_id",
  "mode",
  "scenario",
  "source_id",
  "action",
  "step",
  "form_id",
  "error_code",
  "project",
  "project_id",
  "kind",
  "metric",
  "rating",
]);
const NUMBER_PARAMS = new Set(["depth", "seconds", "step", "value", "moves"]);
let eventSequence = 0;
let initialized = false;
let gaLoaded = false;
let queue = [];
let flushing = false;
let lastPage = null;
let lastTick = Date.now();
let activeSeconds = 0;
let scrollMilestones = new Set();
let cleanupListeners = () => {};
let memoryVisitor = null;
let memorySession = null;
let performanceStarted = false;
let sectionObserver = null;
let sectionTimer;
const observedSections = new Set();

const browser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";
const browserStorage = (name) => {
  try {
    return browser() ? window[name] : null;
  } catch {
    return null;
  }
};
const read = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};
const write = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    /* Private browsing can restrict storage. */
  }
};
const remove = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    /* Ignore unavailable storage. */
  }
};
function parse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
function newId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`
  );
}

export function isExcludedPath(path = "") {
  return /^\/(admin|login|portal)(\/|$)/i.test(path);
}

export function cleanPath(path = "/") {
  const clean = String(path).split(/[?#]/)[0];
  return clean.startsWith("/") && !clean.includes("@") && clean.length <= 200
    ? clean
    : "/";
}

export function sanitizeEventParams(input = {}) {
  const safe = {};
  for (const [key, value] of Object.entries(input)) {
    if (
      STRING_PARAMS.has(key) &&
      typeof value === "string" &&
      !/[\r\n@?]/.test(value)
    ) {
      safe[key] = value.replace(/[^\w .:/#-]/g, "").slice(0, 80);
    } else if (key === "value" && Number.isFinite(value)) {
      safe[key] = Math.max(0, Math.min(60000, Number(value.toFixed(4))));
    } else if (NUMBER_PARAMS.has(key) && Number.isFinite(value)) {
      safe[key] = Math.max(
        0,
        Math.min(
          key === "depth" ? 100 : key === "step" ? 99 : key === "moves" ? 500 : 60,
          Math.round(value),
        ),
      );
    }
  }
  return safe;
}

export function getConsent() {
  if (!browser()) return null;
  const value = read(browserStorage("localStorage"), CONSENT_KEY);
  return ["granted", "denied"].includes(value) ? value : null;
}

export function getInternalTrafficExcluded() {
  return (
    browser() && read(browserStorage("localStorage"), EXCLUSION_KEY) === "true"
  );
}

function isLocal() {
  return (
    browser() &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
  );
}

function canCollect(path = browser() ? window.location.pathname : "/") {
  return (
    browser() &&
    Boolean(gaId || endpoint) &&
    (trackingMode === "cookieless" ? getConsent() !== "denied" : getConsent() === "granted") &&
    !getInternalTrafficExcluded() &&
    !isExcludedPath(path) &&
    !isExcludedPath(window.location.pathname) &&
    (!isLocal() || localEnabled) &&
    !navigator.webdriver &&
    !navigator.globalPrivacyControl &&
    navigator.doNotTrack !== "1" && window.doNotTrack !== "1"
  );
}

export function getAnalyticsStatus() {
  return {
    mode: trackingMode,
    bannerEnabled: trackingMode !== "cookieless" && env.REACT_APP_SHOW_COOKIE_BANNER !== "false",
    gaConfigured: Boolean(gaId),
    dashboardConfigured: Boolean(endpoint),
    consent: getConsent(),
    excluded: getInternalTrafficExcluded(),
    collecting: canCollect(),
  };
}

function consentEvent() {
  if (browser())
    window.dispatchEvent(
      new CustomEvent("karalewich:analytics-consent", { detail: getConsent() }),
    );
}

function forgetIdentifiers() {
  if (!browser()) return;
  remove(browserStorage("localStorage"), VISITOR_KEY);
  remove(browserStorage("sessionStorage"), SESSION_KEY);
  memoryVisitor = null;
  memorySession = null;
  queue = [];
  lastPage = null;
  activeSeconds = 0;
  try {
    const names = document.cookie
      .split(";")
      .map((part) => part.trim().split("=")[0])
      .filter((name) => /^_ga($|_)/.test(name));
    const parts = window.location.hostname.split(".");
    for (const name of names) {
      document.cookie = `${name}=; Max-Age=0; path=/`;
      for (let i = 0; i < parts.length - 1; i += 1) {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${parts.slice(i).join(".")}`;
      }
    }
  } catch {
    /* Restricted contexts can block cookie access too. Collection remains disabled. */
  }
}

export function setConsent(value) {
  if (!browser() || !["granted", "denied"].includes(value)) return;
  write(browserStorage("localStorage"), CONSENT_KEY, value);
  if (gaId)
    window[`ga-disable-${gaId}`] =
      value !== "granted" || getInternalTrafficExcluded();
  if (gaLoaded)
    window.gtag?.("consent", "update", {
      analytics_storage: value,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  if (value !== "granted") forgetIdentifiers();
  else {
    lastTick = Date.now();
    activeSeconds = 0;
    trackPageView(window.location.pathname, document.title);
  }
  consentEvent();
}

export function setInternalTrafficExcluded(value) {
  if (!browser()) return;
  write(browserStorage("localStorage"), EXCLUSION_KEY, String(Boolean(value)));
  if (gaId)
    window[`ga-disable-${gaId}`] = Boolean(value) || getConsent() !== "granted";
  if (value) forgetIdentifiers();
  else trackPageView(window.location.pathname, document.title);
  consentEvent();
}

export function openAnalyticsPreferences() {
  if (browser())
    window.dispatchEvent(new Event("karalewich:open-analytics-preferences"));
}

function identify() {
  const now = Date.now();
  if (trackingMode === "cookieless") {
    if (!memorySession || now - memorySession.lastSeen > SESSION_IDLE_MS) memorySession = {id:newId(), lastSeen:now, returning:false};
    memorySession.lastSeen = now;
    return {visitorId:memorySession.id, sessionId:memorySession.id, returning:false};
  }
  let visitor =
    parse(read(browserStorage("localStorage"), VISITOR_KEY)) || memoryVisitor;
  const existing = Boolean(visitor?.id && visitor.expires > now);
  if (!existing) visitor = { id: newId(), expires: now + VISITOR_LIFETIME_MS };
  let session =
    parse(read(browserStorage("sessionStorage"), SESSION_KEY)) || memorySession;
  if (!session?.id || now - session.lastSeen > SESSION_IDLE_MS) {
    session = { id: newId(), lastSeen: now, returning: existing };
  }
  session.lastSeen = now;
  write(browserStorage("localStorage"), VISITOR_KEY, JSON.stringify(visitor));
  write(browserStorage("sessionStorage"), SESSION_KEY, JSON.stringify(session));
  memoryVisitor = visitor;
  memorySession = session;
  return {
    visitorId: visitor.id,
    sessionId: session.id,
    returning: session.returning,
  };
}

function acquisition() {
  const params = new URLSearchParams(window.location.search);
  const campaign = (key) => {
    const value = params.get(key) || "";
    return /^[\w .-]{1,70}$/.test(value) ? value : "";
  };
  let referrer = "";
  try {
    const url = new URL(document.referrer);
    if (url.hostname !== window.location.hostname)
      referrer = url.hostname.slice(0, 100);
  } catch {
    /* A direct visit has no referrer. */
  }
  const prior = memorySession?.acquisition;
  if (prior) return prior;
  const value = {
    source: campaign("utm_source") || referrer || "direct",
    medium: campaign("utm_medium"),
    campaign: campaign("utm_campaign"),
    referrer,
  };
  if (memorySession) {
    memorySession.acquisition = value;
    if (trackingMode !== "cookieless") write(
      browserStorage("sessionStorage"),
      SESSION_KEY,
      JSON.stringify(memorySession),
    );
  }
  return value;
}

function initGa() {
  if (!gaId || gaLoaded || !canCollect()) return;
  gaLoaded = true;
  window[`ga-disable-${gaId}`] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", gaId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_expires: VISITOR_LIFETIME_MS / 1000,
    page_location: `${window.location.origin}${cleanPath(window.location.pathname)}`,
    page_referrer: acquisition().referrer
      ? `https://${acquisition().referrer}/`
      : "",
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  script.dataset.karalewichAnalytics = "true";
  document.head.appendChild(script);
}

export function trackEvent(name, params = {}, pathOverride) {
  const eventPath = cleanPath(
    pathOverride || (browser() ? window.location.pathname : "/"),
  );
  if (!EVENT_NAMES.has(name) || !canCollect(eventPath)) return false;
  const safeParams = sanitizeEventParams(params);
  const ids = identify();
  const event = {
    id: newId(),
    name,
    ...ids,
    path: eventPath,
    sequence: ++eventSequence,
    collectionMode: trackingMode,
    browser: browserFamily(),
    os: operatingSystem(),
    timestamp: new Date().toISOString(),
    ...acquisition(),
    device:
      window.innerWidth < 640
        ? "mobile"
        : window.innerWidth < 1024
          ? "tablet"
          : "desktop",
    language: (navigator.language || "").slice(0, 20),
    params: safeParams,
  };
  if (name === "page_view") startPerformanceReporting(eventPath);
  initGa();
  if (gaId) {
    window.gtag?.("event", name, {
      ...safeParams,
      page_location: `${window.location.origin}${event.path}`,
      page_title: document.title.slice(0, 120),
      page_referrer: event.referrer ? `https://${event.referrer}/` : "",
      ...(debug ? { debug_mode: true } : {}),
    });
  }
  if (endpoint) {
    queue.push(event);
    if (queue.length > 100) queue.shift();
    if (queue.length >= 15) void flush();
  }
  if (debug) console.info("[Karalewich analytics]", name, safeParams);
  return true;
}

export function trackPageView(
  path = browser() ? window.location.pathname : "/",
  title,
) {
  const next = cleanPath(path);
  if (!browser()) return false;
  const permitted = canCollect(next);
  if (gaId) window[`ga-disable-${gaId}`] = !permitted;
  if (isExcludedPath(next)) {
    lastPage = null;
    activeSeconds = 0;
    lastTick = Date.now();
    return false;
  }
  if (!permitted || lastPage === next) return false;
  if (title && typeof title === "string") {
    /* The caller owns document.title; no content is collected here. */
  }
  if (lastPage && activeSeconds > 0)
    trackEvent("engagement", { seconds: activeSeconds }, lastPage);
  activeSeconds = 0;
  lastTick = Date.now();
  scrollMilestones = new Set();
  lastPage = next;
  observeSections(next);
  return trackEvent("page_view", {}, next);
}

async function flush(beacon = false) {
  if (!endpoint || !queue.length || flushing || !canCollect()) return;
  const batch = queue.splice(0, 20);
  const body = JSON.stringify({ events: batch });
  if (beacon && navigator.sendBeacon) {
    const sent = navigator.sendBeacon(
      endpoint,
      new Blob([body], { type: "application/json" }),
    );
    if (sent) return;
  }
  flushing = true;
  try {
    const result = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    });
    if (!result.ok && debug)
      console.warn("[Karalewich analytics] Collection returned", result.status);
  } catch {
    // Never block the visitor or retry indefinitely when collection is unavailable.
    if (debug) console.warn("[Karalewich analytics] Collector unavailable");
  } finally {
    flushing = false;
  }
}

export function startAnalytics() {
  if (!browser() || initialized) return () => {};
  initialized = true;
  if (trackingMode === "cookieless") {
    remove(browserStorage("localStorage"), VISITOR_KEY);
    remove(browserStorage("sessionStorage"), SESSION_KEY);
  }
  const onRuntimeError = event => {
    if (event.target && event.target !== window) {
      const kind = event.target.tagName?.toLowerCase();
      if (["img", "script", "link"].includes(kind)) trackEvent("client_error", {error_code:"resource_load", kind});
    } else trackEvent("client_error", {error_code:"javascript"});
  };
  const onRejection = () => trackEvent("client_error", {error_code:"unhandled_promise"});
  const onClick = (event) => {
    const el = event.target?.closest?.("[data-analytics]");
    if (!el) return;
    const name = el.dataset.analyticsEvent || "cta_click";
    trackEvent(name, {
      target: el.dataset.analytics,
      placement: el.dataset.analyticsPlacement || "",
      kind: el.tagName.toLowerCase(),
    });
  };
  const onScroll = () => {
    if (!canCollect()) return;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const depth = max > 0 ? Math.round((window.scrollY / max) * 100) : 0;
    for (const milestone of [25, 50, 75, 100]) {
      if (depth >= milestone && !scrollMilestones.has(milestone)) {
        scrollMilestones.add(milestone);
        trackEvent("scroll_depth", { depth: milestone });
      }
    }
  };
  const tick = () => {
    const now = Date.now();
    if (
      canCollect() &&
      document.visibilityState === "visible" &&
      document.hasFocus()
    ) {
      activeSeconds += Math.min(
        15,
        Math.max(0, Math.round((now - lastTick) / 1000)),
      );
      if (activeSeconds >= 30) {
        trackEvent("engagement", { seconds: activeSeconds });
        activeSeconds = 0;
      }
    }
    lastTick = now;
  };
  const onHidden = () => {
    if (document.visibilityState !== "hidden") {
      lastTick = Date.now();
      return;
    }
    tick();
    if (activeSeconds > 0) {
      trackEvent("engagement", { seconds: activeSeconds });
      activeSeconds = 0;
    }
    void flush(true);
  };
  const onPageHide = () => {
    if (activeSeconds > 0) trackEvent("engagement", { seconds: activeSeconds });
    activeSeconds = 0;
    void flush(true);
  };
  const onStorage = (event) => {
    if (![CONSENT_KEY, EXCLUSION_KEY].includes(event.key)) return;
    const allowed = canCollect();
    if (gaId) window[`ga-disable-${gaId}`] = !allowed;
    if (!allowed) {
      if (gaLoaded)
        window.gtag?.("consent", "update", { analytics_storage: "denied" });
      forgetIdentifiers();
    } else {
      if (gaLoaded)
        window.gtag?.("consent", "update", { analytics_storage: "granted" });
      trackPageView(window.location.pathname, document.title);
    }
    consentEvent();
  };
  window.addEventListener("error", onRuntimeError, true);
  window.addEventListener("unhandledrejection", onRejection);
  document.addEventListener("click", onClick, true);
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("storage", onStorage);
  const heartbeat = window.setInterval(tick, 15000);
  const sender = window.setInterval(() => void flush(), 10000);
  trackPageView(window.location.pathname, document.title);
  cleanupListeners = () => {
    window.removeEventListener("error", onRuntimeError, true);
    window.removeEventListener("unhandledrejection", onRejection);
    sectionObserver?.disconnect();
    window.clearTimeout(sectionTimer);
    document.removeEventListener("click", onClick, true);
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("visibilitychange", onHidden);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("storage", onStorage);
    window.clearInterval(heartbeat);
    window.clearInterval(sender);
    initialized = false;
  };
  return cleanupListeners;
}

function browserFamily() {
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Firefox|FxiOS/.test(ua)) return "Firefox";
  if (/Chrome|CriOS/.test(ua)) return "Chrome";
  if (/Safari/.test(ua)) return "Safari";
  return "Other";
}
function operatingSystem() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  if (/Macintosh|Mac OS/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Other";
}
function startPerformanceReporting(path) {
  if (performanceStarted || typeof window.PerformanceObserver !== "function") return;
  performanceStarted = true;
  void import("web-vitals").then(({ onLCP, onINP, onCLS }) => {
    const report = metric => {
      if (!["LCP", "INP", "CLS"].includes(metric.name)) return;
      trackEvent("web_vital", {metric:metric.name.toLowerCase(),value:metric.value,rating:metric.rating}, path);
      void flush();
    };
    onLCP(report); onINP(report); onCLS(report);
  }).catch(() => {});
}
function observeSections(path) {
  if (!browser() || typeof IntersectionObserver !== "function") return;
  sectionObserver?.disconnect();
  window.clearTimeout(sectionTimer);
  sectionTimer = window.setTimeout(() => {
    if (!canCollect(path) || cleanPath(window.location.pathname) !== path) return;
    sectionObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const section = entry.target.dataset.analyticsSection;
        const key = `${path}:${section}`;
        if (!observedSections.has(key)) {
          observedSections.add(key);
          trackEvent("section_view", {section}, path);
        }
        sectionObserver.unobserve(entry.target);
      }
    }, {threshold:0.25});
    document.querySelectorAll("main [data-analytics-section]").forEach(element => sectionObserver.observe(element));
  }, 600);
}
